// Map utilities for Tiled map integration (image-based map with polygon collisions)

export const loadMap = async () => {
  const response = await fetch('/map.json');
  const tiledMap = await response.json();
  
  // Find the image layer
  const imageLayer = tiledMap.layers.find(layer => layer.type === 'imagelayer');
  
  if (!imageLayer || !imageLayer.image) {
    console.warn('No image layer found in map');
    return parseMapData(tiledMap, null);
  }
  
  // Load background image
  const backgroundImage = new Image();
  backgroundImage.src = `/${imageLayer.image}`;
  
  await new Promise((resolve, reject) => {
    backgroundImage.onload = resolve;
    backgroundImage.onerror = () => reject(new Error(`Failed to load background image: ${imageLayer.image}`));
  });
  
  console.log('Background image loaded:', imageLayer.image, `(${backgroundImage.width}x${backgroundImage.height})`);
  
  return parseMapData(tiledMap, backgroundImage);
};

export const parseMapData = (tiledMap, backgroundImage) => {
  // Calculate map dimensions (orthogonal maps only)
  const mapWidth = tiledMap.width * tiledMap.tilewidth;   // 60 * 32 = 1920px
  const mapHeight = tiledMap.height * tiledMap.tileheight; // 60 * 32 = 1920px
  
  console.log(`Map dimensions: ${mapWidth}x${mapHeight} pixels (${tiledMap.width}x${tiledMap.height} tiles of ${tiledMap.tilewidth}x${tiledMap.tileheight})`);
  
  // Find layers
  const objectsLayer = tiledMap.layers.find(l => l.name === 'Objects');
  const imageLayer = tiledMap.layers.find(l => l.type === 'imagelayer');
  
  // Parse polygon collision objects
  const collisionPolygons = [];
  // Parse audience seat objects: name matches "seat N"
  const seats = [];
  const seatPositionsSeen = new Set();
  // Parse team seat objects: name matches "team A seat N" or "team B seat N"
  const teamSeats = [];
  const teamSeatPositionsSeen = new Set();

  if (objectsLayer && objectsLayer.objects) {
    objectsLayer.objects.forEach(obj => {
      if (obj.text) return;

      if (obj.polygon && Array.isArray(obj.polygon)) {
        const absolutePoints = obj.polygon.map(point => ({
          x: obj.x + point.x,
          y: obj.y + point.y
        }));
        collisionPolygons.push({ id: obj.id, name: obj.name || '', points: absolutePoints, type: 'polygon' });
      } else if ((Number(obj.width) > 0 || Number(obj.height) > 0)) {
        const trimmedName = typeof obj.name === 'string' ? obj.name.trim() : '';
        const isAudienceSeat = /^seat \d+$/i.test(trimmedName);
        const isTeamSeat = /^team [AB] seat \d+$/i.test(trimmedName);

        if (isAudienceSeat) {
          const key = `${Math.round(obj.x)},${Math.round(obj.y)}`;
          if (!seatPositionsSeen.has(key)) {
            seatPositionsSeen.add(key);
            seats.push({
              id: obj.id,
              name: trimmedName,
              x: Math.round(obj.x),
              y: Math.round(obj.y),
              width: Math.round(obj.width),
              height: Math.round(obj.height),
              cx: Math.round(obj.x + obj.width / 2),
              cy: Math.round(obj.y + obj.height / 2),
            });
          }
        } else if (isTeamSeat) {
          // "team A seat 1" → parts: ['team','A','seat','1']
          const parts = trimmedName.split(' ');
          const team = parts[1].toUpperCase(); // 'A' or 'B'
          const seatNum = parseInt(parts[3], 10);
          const key = `${Math.round(obj.x)},${Math.round(obj.y)}`;
          if (!teamSeatPositionsSeen.has(key)) {
            teamSeatPositionsSeen.add(key);
            teamSeats.push({
              id: obj.id,
              name: trimmedName,
              team,
              seatNum,
              x: Math.round(obj.x),
              y: Math.round(obj.y),
              width: Math.round(obj.width),
              height: Math.round(obj.height),
              cx: Math.round(obj.x + obj.width / 2),
              cy: Math.round(obj.y + obj.height / 2),
            });
          }
        } else {
          const collisionRect = rectangleCollision(obj, trimmedName);
          if (collisionRect) collisionPolygons.push(collisionRect);
        }
      }
    });
    console.log(`Found ${collisionPolygons.length} collision polygons, ${seats.length} audience seats, ${teamSeats.length} team seats`);
  }

  const spawnPoints = findSpawnPoints();
  
  return {
    width: mapWidth,
    height: mapHeight,
    tileWidth: tiledMap.tilewidth,
    tileHeight: tiledMap.tileheight,
    tilesWide: tiledMap.width,
    tilesHigh: tiledMap.height,
    orientation: tiledMap.orientation || 'orthogonal',
    backgroundImage: backgroundImage,  // The loaded background image
    collisionPolygons: collisionPolygons,
    seats,
    teamSeats,
    spawnPoints,
    backgroundColor: '#000000'
  };
};

// Stage barrier in map.json: y=1544, height=48 → collision through y=1592.
// Feet (walk point) sit 1px below so spawn is walkable and flush with the box.
export const DEFAULT_SPAWN_Y = 1593;
export const DEFAULT_SPAWN = { x: 960, y: DEFAULT_SPAWN_Y };

/** Zero-height Tiled lines would otherwise be skipped; thicken enough to catch a run step. */
const LINE_COLLISION_THICKNESS = 16;

function rectangleCollision(obj, trimmedName) {
  let x = Number(obj.x) || 0;
  let y = Number(obj.y) || 0;
  let width = Number(obj.width) || 0;
  let height = Number(obj.height) || 0;
  const isLineBarrier = /^stage boundary$/i.test(trimmedName);

  if (width <= 0 && height <= 0) return null;
  if ((width <= 0 || height <= 0) && !isLineBarrier) return null;

  if (isLineBarrier || width <= 0 || height <= 0) {
    if (isLineBarrier && height < LINE_COLLISION_THICKNESS && width >= LINE_COLLISION_THICKNESS) {
      // Stage boundary is the south edge of the forbidden zone (toward the TV).
      height = y;
      y = 0;
    } else if (height < LINE_COLLISION_THICKNESS && width >= LINE_COLLISION_THICKNESS) {
      height = LINE_COLLISION_THICKNESS;
      y -= height;
    } else if (width < LINE_COLLISION_THICKNESS && height >= LINE_COLLISION_THICKNESS) {
      width = LINE_COLLISION_THICKNESS;
    }
  }

  return {
    id: obj.id,
    name: obj.name || '',
    points: [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height }
    ],
    type: 'rectangle'
  };
}

const findSpawnPoints = () => {
  // Concourse just below the stage barrier (ends y:1592), above the first seat row (y:1640).
  console.log('Using safe spawn points for 1920x1920 map');
  return [
    { x: 960, y: DEFAULT_SPAWN_Y },
    { x: 300, y: DEFAULT_SPAWN_Y },
    { x: 1620, y: DEFAULT_SPAWN_Y },
    { x: 550, y: DEFAULT_SPAWN_Y },
    { x: 1370, y: DEFAULT_SPAWN_Y },
    { x: 800, y: DEFAULT_SPAWN_Y },
  ];
};

// Helper function: Check if point is inside a polygon using ray casting algorithm
const pointInPolygon = (x, y, polygon) => {
  let inside = false;
  const points = polygon.points;
  
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    
    const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
};

// Helper: Get closest point on a line segment to a given point
const closestPointOnSegment = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  
  if (lengthSquared === 0) return { x: x1, y: y1 };
  
  // Project point onto line segment
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t)); // Clamp to segment
  
  return {
    x: x1 + t * dx,
    y: y1 + t * dy
  };
};

// Helper: Find closest edge of polygon and calculate slide vector
const getSlideVector = (fromX, fromY, toX, toY, polygon) => {
  const points = polygon.points;
  let minDist = Infinity;
  let closestPoint = null;
  let edgeNormalX = 0;
  let edgeNormalY = 0;
  
  // Find closest edge
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const closest = closestPointOnSegment(toX, toY, points[j].x, points[j].y, points[i].x, points[i].y);
    const dist = Math.sqrt((toX - closest.x) ** 2 + (toY - closest.y) ** 2);
    
    if (dist < minDist) {
      minDist = dist;
      closestPoint = closest;
      
      // Calculate edge normal (perpendicular to edge, pointing outward)
      const edgeX = points[i].x - points[j].x;
      const edgeY = points[i].y - points[j].y;
      const edgeLen = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
      
      if (edgeLen > 0) {
        // Normal perpendicular to edge
        edgeNormalX = -edgeY / edgeLen;
        edgeNormalY = edgeX / edgeLen;
        
        // Make sure normal points outward (away from polygon center)
        const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
        const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
        const midX = (points[i].x + points[j].x) / 2;
        const midY = (points[i].y + points[j].y) / 2;
        const toCenter = (centerX - midX) * edgeNormalX + (centerY - midY) * edgeNormalY;
        
        if (toCenter > 0) {
          edgeNormalX = -edgeNormalX;
          edgeNormalY = -edgeNormalY;
        }
      }
    }
  }
  
  // Calculate movement vector
  const moveX = toX - fromX;
  const moveY = toY - fromY;
  
  // Project movement onto edge (remove component perpendicular to edge)
  const dotProduct = moveX * edgeNormalX + moveY * edgeNormalY;
  
  // If moving into the edge, slide along it
  if (dotProduct < 0) {
    return {
      x: fromX + (moveX - dotProduct * edgeNormalX),
      y: fromY + (moveY - dotProduct * edgeNormalY)
    };
  }
  
  // If moving away from edge, allow full movement
  return { x: toX, y: toY };
};

function ignoredCollision(polygon, options) {
  const names = options && options.ignoreNames;
  if (!names || !names.length || !polygon || !polygon.name) return false;
  return names.includes(polygon.name);
}

export const isWalkable = (x, y, mapData, options = {}) => {
  // Out of bounds check
  if (x < 0 || x > mapData.width || y < 0 || y > mapData.height) {
    return false;
  }
  
  // Check polygon collisions
  if (mapData.collisionPolygons && mapData.collisionPolygons.length > 0) {
    for (const polygon of mapData.collisionPolygons) {
      if (ignoredCollision(polygon, options)) continue;
      if (pointInPolygon(x, y, polygon)) {
        return false; // Inside a collision polygon, not walkable
      }
    }
  }
  
  return true;
};

// New function: Resolve collision with sliding
export const resolveCollision = (fromX, fromY, toX, toY, mapData, options = {}) => {
  // Out of bounds check
  if (toX < 0 || toX > mapData.width || toY < 0 || toY > mapData.height) {
    return { x: fromX, y: fromY, collided: true };
  }
  
  // Check polygon collisions and apply sliding
  if (mapData.collisionPolygons && mapData.collisionPolygons.length > 0) {
    for (const polygon of mapData.collisionPolygons) {
      if (ignoredCollision(polygon, options)) continue;
      if (pointInPolygon(toX, toY, polygon)) {
        // Collision detected, calculate slide
        const slidePos = getSlideVector(fromX, fromY, toX, toY, polygon);
        
        // Verify slide position is walkable
        if (isWalkable(slidePos.x, slidePos.y, mapData, options)) {
          return { x: slidePos.x, y: slidePos.y, collided: true };
        }
        
        // If slide position also collides, stay at original position
        return { x: fromX, y: fromY, collided: true };
      }
    }
  }
  
  // No collision, allow full movement
  return { x: toX, y: toY, collided: false };
};

export const drawMap = (ctx, mapData, cameraX, cameraY, viewportWidth, viewportHeight) => {
  // Fill background color
  ctx.fillStyle = mapData.backgroundColor;
  ctx.fillRect(0, 0, viewportWidth, viewportHeight);
  
  // Save context for camera transform
  ctx.save();
  ctx.translate(-cameraX, -cameraY);
  
  // Draw background image if available
  if (mapData.backgroundImage) {
    ctx.drawImage(mapData.backgroundImage, 0, 0, mapData.width, mapData.height);
  }
  
  ctx.restore();
};

export const drawMinimap = (ctx, mapData, players, myPosition, viewportWidth, viewportHeight) => {
  const minimapWidth = 120;
  // Calculate proportional height based on actual map dimensions
  const mapAspect = mapData.height / mapData.width;
  const minimapHeight = Math.min(480, minimapWidth * mapAspect); // Cap at 480px
  const minimapX = viewportWidth - minimapWidth - 10;
  const minimapY = 10;
  
  // Minimap background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(minimapX, minimapY, minimapWidth, minimapHeight);
  
  ctx.save();
  ctx.translate(minimapX, minimapY);
  
  // Draw map tiles on minimap (simplified) - just show map extent
  ctx.fillStyle = 'rgba(100, 100, 150, 0.2)';
  ctx.fillRect(0, 0, minimapWidth, minimapHeight);
  
  // Draw all players on minimap
  Object.entries(players).forEach(([id, player]) => {
    const mmX = (player.x / mapData.width) * minimapWidth;
    const mmY = (player.y / mapData.height) * minimapHeight;
    ctx.fillStyle = 'white';
    ctx.fillRect(mmX - 2, mmY - 2, 4, 4);
  });
  
  // Draw self (yellow square)
  const selfX = (myPosition.x / mapData.width) * minimapWidth;
  const selfY = (myPosition.y / mapData.height) * minimapHeight;
  ctx.fillStyle = 'yellow';
  ctx.fillRect(selfX - 3, selfY - 3, 6, 6);
  
  // Draw viewport indicator (red box)
  const vpX = ((myPosition.x - viewportWidth / 2) / mapData.width) * minimapWidth;
  const vpY = ((myPosition.y - viewportHeight / 2) / mapData.height) * minimapHeight;
  const vpW = (viewportWidth / mapData.width) * minimapWidth;
  const vpH = (viewportHeight / mapData.height) * minimapHeight;
  ctx.strokeStyle = 'red';
  ctx.lineWidth = 2;
  ctx.strokeRect(vpX, vpY, vpW, vpH);
  
  ctx.restore();
  
  // Minimap border
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 2;
  ctx.strokeRect(minimapX, minimapY, minimapWidth, minimapHeight);
};
